import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { billingApi } from '../lib/api';
import { Spinner, Alert, UsageMeter } from '@rankhelm/ui';
import type { Plan, Subscription, UsageSummary } from '@rankhelm/shared';

export function BillingPage() {
  const { account } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [plansRes, usageRes, subRes] = await Promise.all([
        billingApi.plans(),
        billingApi.usage(),
        billingApi.subscription(),
      ]);
      setPlans(plansRes.data);
      setUsage(usageRes.data);
      setSubscription(subRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }

  async function handleCheckout(planId: string) {
    setCheckoutLoading(planId);
    try {
      const res = await billingApi.createCheckout({
        plan_id: planId,
        success_url: `${window.location.origin}/billing?success=true`,
        cancel_url: `${window.location.origin}/billing?canceled=true`,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  }

  async function handleManage() {
    try {
      const res = await billingApi.createPortal({
        return_url: `${window.location.origin}/billing`,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    }
  }

  if (loading) {
    return <Spinner message="Loading billing..." fullPage />;
  }

  const currentTier = usage?.plan_tier || account?.plan_tier || 'starter';

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Billing & Usage</h1>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="space-y-8 max-w-4xl">
        {/* Current Plan & Usage */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-white">Current Plan</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-helm-500/15 text-helm-400 capitalize">
                  {usage?.plan_name || currentTier}
                </span>
                {subscription?.status && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    subscription.status === 'active' ? 'bg-green-500/15 text-green-400' :
                    subscription.status === 'trialing' ? 'bg-blue-500/15 text-blue-400' :
                    subscription.status === 'past_due' ? 'bg-amber-500/15 text-amber-400' :
                    'bg-slate-500/15 text-slate-400'
                  }`}>
                    {subscription.status}
                  </span>
                )}
              </div>
            </div>
            {subscription?.stripe_customer_id && (
              <button
                onClick={handleManage}
                className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 font-medium rounded-lg border border-navy-600 transition-colors text-sm"
              >
                Manage Subscription
              </button>
            )}
          </div>

          {usage && (
            <div className="space-y-4">
              <UsageMeter label="Workspaces" used={usage.resources.workspaces.used} limit={usage.resources.workspaces.limit} />
              <UsageMeter label="Articles this period" used={usage.resources.articles.used} limit={usage.resources.articles.limit} />
              <UsageMeter label="AI Generations this period" used={usage.resources.ai_generations.used} limit={usage.resources.ai_generations.limit} />
              <UsageMeter label="Storage (GB)" used={usage.resources.storage_gb.used} limit={usage.resources.storage_gb.limit} />
            </div>
          )}

          {usage && (
            <p className="text-xs text-slate-500 mt-4">
              Billing period: {new Date(usage.period_start).toLocaleDateString()} &mdash; {new Date(usage.period_end).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Plan Comparison */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Available Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => {
              const isCurrent = plan.tier === currentTier;
              const isEnterprise = plan.tier === 'enterprise';
              return (
                <div
                  key={plan.id}
                  className={`bg-navy-900 border rounded-xl p-5 flex flex-col ${
                    isCurrent ? 'border-helm-500 ring-1 ring-helm-500/30' : 'border-navy-700'
                  }`}
                >
                  <h3 className="text-white font-semibold text-lg">{plan.name}</h3>
                  <div className="mt-2">
                    {isEnterprise ? (
                      <span className="text-2xl font-bold text-white">Custom</span>
                    ) : (
                      <>
                        <span className="text-2xl font-bold text-white">${(plan.price_cents / 100).toFixed(0)}</span>
                        <span className="text-slate-400 text-sm">/mo</span>
                      </>
                    )}
                  </div>
                  <ul className="mt-4 space-y-2 text-sm text-slate-400 flex-1">
                    <li>{plan.workspace_limit >= 999999 ? 'Unlimited' : plan.workspace_limit} workspaces</li>
                    <li>{plan.articles_per_month >= 999999 ? 'Unlimited' : plan.articles_per_month} articles/mo</li>
                    <li>{plan.ai_generations_per_month >= 999999 ? 'Unlimited' : plan.ai_generations_per_month} AI gens/mo</li>
                    <li>{plan.storage_gb} GB storage</li>
                  </ul>
                  <div className="mt-4">
                    {isCurrent ? (
                      <div className="text-center text-sm text-helm-400 font-medium py-2">Current Plan</div>
                    ) : isEnterprise ? (
                      <a
                        href="mailto:support@rankhelm.io?subject=Enterprise%20Plan%20Inquiry"
                        className="block text-center px-4 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 font-medium rounded-lg border border-navy-600 transition-colors text-sm"
                      >
                        Contact Sales
                      </a>
                    ) : (
                      <button
                        onClick={() => handleCheckout(plan.id)}
                        disabled={checkoutLoading === plan.id}
                        className="w-full px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white font-medium rounded-lg transition-colors text-sm disabled:opacity-50"
                      >
                        {checkoutLoading === plan.id ? 'Redirecting...' : 'Upgrade'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
